import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useHotel } from "../contexts/HotelContext";
import {
  useTenantsQuery,
  useCreateTenantMutation,
  useDeleteTenantMutation,
  useSyncStripeSubscriptionsMutation,
} from "../hooks/useTenants";
import { useBrandsQuery } from "../hooks/useBrands";
import {
  Tenant,
  CreateTenantPayload,
} from "../services/api";
import type { UnifiedSubscriptionView } from "../types/unifiedSubscription";
import Layout from "../components/Layout";
import TenantMigrationModal from "./admin/billing/components/TenantMigrationModal";
import { ModuleDisplay } from "../components/tenants/ModuleDisplay";
import { TenantBasicInfoForm } from "../components/tenants/TenantBasicInfoForm";
import { CustomDomainsManager } from "../components/tenants/CustomDomainsManager";
import { ModuleManager } from "../components/tenants/ModuleManager";

// C4: getTenantMigratedAt removed — migratedAt now comes from backend via tenant.migratedAt field

// Subscription Status Badge Component
const SubscriptionStatusBadge: React.FC<{
  status: string | null;
}> = ({ status }) => {
  if (!status) {
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-500">
        Brak
      </span>
    );
  }

  const statusConfig: Record<string, { label: string; color: string }> = {
    active: { label: "Aktywna", color: "bg-green-100 text-green-800" },
    trialing: { label: "Trial", color: "bg-blue-100 text-blue-800" },
    past_due: { label: "Zaległa", color: "bg-yellow-100 text-yellow-800" },
    canceled: { label: "Anulowana", color: "bg-red-100 text-red-800" },
    unpaid: { label: "Niezapłacona", color: "bg-orange-100 text-orange-800" },
    suspended: {
      label: "Zawieszona",
      color: "bg-neutral-200 text-neutral-700",
    },
    incomplete: {
      label: "Niekompletna",
      color: "bg-neutral-100 text-neutral-500",
    },
    expired: { label: "Wygasła", color: "bg-neutral-200 text-neutral-600" },
  };

  const config = statusConfig[status] ?? { label: status, color: "bg-neutral-100 text-neutral-500" };

  return (
    <span
      className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}
    >
      {config.label}
    </span>
  );
};

// Helper: Get subscription end date
const getSubscriptionEndDate = (
  subscription: UnifiedSubscriptionView | null | undefined
): string => {
  if (!subscription) return "-";

  // Priority: trial end > current period end
  const endDate = subscription.trialEndsAt || subscription.currentPeriodEnd;

  if (!endDate) return "-";

  const date = new Date(endDate);
  return date.toLocaleDateString("pl-PL", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const TenantManagement: React.FC = () => {
  const { user } = useHotel();
  const navigate = useNavigate();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<Tenant | null>(
    null
  );
  // Tenant billing migration modal state (AC-A5, AC-A6, AC-A7)
  const [migratingTenant, setMigratingTenant] = useState<Tenant | null>(null);

  const { data: tenants, isLoading, error } = useTenantsQuery();
  const createTenantMutation = useCreateTenantMutation();
  const deleteTenantMutation = useDeleteTenantMutation();
  const syncSubscriptionsMutation = useSyncStripeSubscriptionsMutation();

  // Redirect non-admin users
  if (user?.role !== "admin") {
    navigate("/");
    return null;
  }

  const handleCreateTenant = (tenantData: CreateTenantPayload) => {
    createTenantMutation.mutate(tenantData, {
      onSuccess: () => {
        setIsCreateModalOpen(false);
      },
    });
  };

  const handleDeleteTenant = (tenant: Tenant) => {
    deleteTenantMutation.mutate(tenant.id, {
      onSuccess: () => {
        setShowDeleteConfirm(null);
      },
    });
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <h3 className="text-red-800 font-medium">Error loading tenants</h3>
          <p className="text-red-600 text-sm mt-1">
            {error instanceof Error ? error.message : "Unknown error occurred"}
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Tenants</h1>
            <p className="text-neutral-600 mt-1">
              Zarządzaj tenantami, użytkownikami i modułami systemu
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => syncSubscriptionsMutation.mutate()}
              disabled={syncSubscriptionsMutation.isPending}
              className="bg-primary-700 hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
            >
              <svg
                aria-hidden="true"
                className={`w-4 h-4 ${syncSubscriptionsMutation.isPending ? "animate-spin" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              <span>
                {syncSubscriptionsMutation.isPending
                  ? "Synchronizacja..."
                  : "Sync Stripe Subscriptions"}
              </span>
            </button>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Dodaj Tenanta
            </button>
          </div>
        </div>

        {/* Tenants Table */}
        <div className="bg-white shadow-sm rounded-lg border border-neutral-200">
          <div className="px-6 py-4 border-b border-neutral-200">
            <h2 className="text-lg font-semibold text-neutral-900">
              Lista Tenantów ({tenants?.length || 0})
            </h2>
          </div>

          {tenants && tenants.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Nazwa
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      NIP
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Brand
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Plan
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Kończy się
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Moduły
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Migracja
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Utworzono
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Akcje
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {tenants.map((tenant) => {
                    const activeSubscription = tenant.activeSubscription ?? null;
                    // C4: Inline migratedAt computation (helper getTenantMigratedAt removed per spec C4).
                    // TenantManagement uses legacy /tenants endpoint where migratedAt comes from subscriptions[].
                    const migratedAt = tenant.subscriptions
                      ?.filter((s) => !!s.migratedAt)
                      .sort((a, b) => new Date(b.migratedAt!).getTime() - new Date(a.migratedAt!).getTime())[0]
                      ?.migratedAt ?? null;

                    return (
                      <tr key={tenant.id} className="hover:bg-neutral-50">
                        <td className="px-3 py-2 text-left whitespace-nowrap">
                          <button
                            onClick={() => navigate(`/admin/tenants/${tenant.id}`)}
                            className="text-xs font-medium text-primary-700 hover:text-primary-800 hover:underline text-left"
                          >
                            {tenant.name}
                          </button>
                        </td>

                        <td className="px-3 py-2 text-left whitespace-nowrap">
                          <div className="text-neutral-600 font-mono text-xs">
                            {tenant.nip || (
                              <span className="text-neutral-400 italic">
                                Brak
                              </span>
                            )}
                          </div>
                        </td>

                        <td className="px-3 py-2 text-left whitespace-nowrap">
                          {tenant.brand ? (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                              style={{
                                backgroundColor: `${tenant.brand.primaryColor}20`,
                                color: tenant.brand.primaryColor,
                              }}
                            >
                              {tenant.brand.displayName}
                            </span>
                          ) : (
                            <span className="text-neutral-400 text-xs italic">
                              Brak
                            </span>
                          )}
                        </td>

                        <td className="px-3 py-2 text-left whitespace-nowrap">
                          <SubscriptionStatusBadge
                            status={activeSubscription?.status || null}
                          />
                        </td>

                        <td className="px-3 py-2 text-left whitespace-nowrap">
                          <div className="text-xs text-neutral-900">
                            {activeSubscription?.productName || (
                              <span className="text-neutral-400 italic">
                                Brak
                              </span>
                            )}
                          </div>
                        </td>

                        <td className="px-3 py-2 text-left whitespace-nowrap text-xs text-neutral-600">
                          {getSubscriptionEndDate(activeSubscription)}
                        </td>

                        <td className="px-3 py-2 text-left">
                          <ModuleDisplay
                            enabledModules={tenant.enabledModules}
                          />
                        </td>

                        {/* Migracja (AC-A5) */}
                        <td className="px-3 py-2 text-left whitespace-nowrap">
                          {migratedAt ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {new Date(migratedAt).toLocaleDateString("pl-PL")}
                            </span>
                          ) : (
                            <button
                              onClick={() => setMigratingTenant(tenant)}
                              className="text-xs font-medium text-amber-600 hover:text-amber-900 transition-colors"
                              title="Migracja do nowego systemu billing — operacja nieodwracalna"
                            >
                              Migruj
                            </button>
                          )}
                        </td>

                        <td className="px-3 py-2 text-left whitespace-nowrap text-xs text-neutral-500">
                          {new Date(tenant.createdAt).toLocaleDateString(
                            "pl-PL"
                          )}
                        </td>

                        <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium space-x-2">
                          <button
                            onClick={() => setEditingTenant(tenant)}
                            className="text-primary-600 hover:text-primary-900 transition-colors"
                          >
                            Edytuj
                          </button>
                          <button
                            onClick={() => setShowDeleteConfirm(tenant)}
                            className="text-red-600 hover:text-red-900 transition-colors"
                          >
                            Usuń
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-6 py-12 text-center">
              <div className="text-neutral-400 text-lg mb-2">Brak tenantów</div>
              <p className="text-neutral-500 text-sm">
                Dodaj pierwszego tenanta, aby rozpocząć zarządzanie systemem
              </p>
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="mt-4 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Dodaj Tenanta
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Create Tenant Modal */}
      {isCreateModalOpen && (
        <CreateTenantModal
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={handleCreateTenant}
          isLoading={createTenantMutation.isPending}
        />
      )}

      {/* Edit Tenant Modal — C2: uses shared TenantBasicInfoForm + CustomDomainsManager + ModuleManager */}
      {editingTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-neutral-200">
              <h3 className="text-lg font-semibold text-neutral-900">
                Edytuj Tenanta: {editingTenant.name}
              </h3>
            </div>
            <div className="p-6 space-y-6">
              <TenantBasicInfoForm
                tenant={editingTenant}
                onSuccess={() => setEditingTenant(null)}
              />
              <div className="border-t border-neutral-200 pt-6">
                <CustomDomainsManager tenant={editingTenant} />
              </div>
              <div className="border-t border-neutral-200 pt-6">
                <ModuleManager tenant={editingTenant} />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-neutral-200 flex justify-end">
              <button
                onClick={() => setEditingTenant(null)}
                className="px-4 py-2 text-neutral-600 hover:text-neutral-800 font-medium transition-colors"
              >
                Zamknij
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <DeleteConfirmModal
          tenant={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(null)}
          onConfirm={() => handleDeleteTenant(showDeleteConfirm)}
          isLoading={deleteTenantMutation.isPending}
        />
      )}

      {/* Tenant Billing Migration Modal (AC-A5, AC-A6, AC-A7) */}
      {migratingTenant && (
        <TenantMigrationModal
          tenantId={migratingTenant.id}
          tenantName={migratingTenant.name}
          onClose={() => setMigratingTenant(null)}
          onMigrationComplete={() => setMigratingTenant(null)}
        />
      )}
    </Layout>
  );
};

// Delete Confirmation Modal Component
const DeleteConfirmModal: React.FC<{
  tenant: Tenant;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}> = ({ tenant, onClose, onConfirm, isLoading }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-neutral-200">
          <h3 className="text-lg font-semibold text-red-900">Usuń Tenanta</h3>
        </div>

        <div className="p-6">
          <p className="text-neutral-600 mb-4">
            Czy na pewno chcesz usunąć tenanta <strong>"{tenant.name}"</strong>?
          </p>
          <p className="text-sm text-red-600 mb-4">
            Ta operacja jest nieodwracalna i usunie wszystkie powiązane dane.
          </p>

          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-neutral-600 hover:text-neutral-800 font-medium transition-colors"
            >
              Anuluj
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-4 py-2 rounded-md font-medium transition-colors"
            >
              {isLoading ? "Usuwanie..." : "Usuń"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Create Tenant Modal Component
const CreateTenantModal: React.FC<{
  onClose: () => void;
  onSubmit: (data: CreateTenantPayload) => void;
  isLoading: boolean;
}> = ({ onClose, onSubmit, isLoading }) => {
  const { data: brands } = useBrandsQuery();
  const [formData, setFormData] = useState<CreateTenantPayload>({
    name: "",
    subdomain: "",
    nip: "",
    customDomains: [],
    enabledModules: [],
  });

  const [createUser, setCreateUser] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleUserToggle = (checked: boolean) => {
    setCreateUser(checked);
    if (!checked) {
      // Clear user fields when unchecked
      setFormData({
        ...formData,
        userEmail: undefined,
        userPassword: undefined,
        userFirstName: undefined,
        userLastName: undefined,
        userRole: undefined,
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-900">
            Dodaj Tenanta
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Nazwa *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Nazwa tenanta"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Subdomena *
            </label>
            <input
              type="text"
              required
              value={formData.subdomain}
              onChange={(e) =>
                setFormData({ ...formData, subdomain: e.target.value })
              }
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono"
              placeholder="subdomena"
              pattern="^[a-z0-9\-]+$"
              title="Tylko małe litery, cyfry i myślniki"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              NIP firmy *
            </label>
            <input
              type="text"
              required
              value={formData.nip}
              onChange={(e) =>
                setFormData({ ...formData, nip: e.target.value })
              }
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="1234567890 lub PL1234567890"
            />
            <p className="text-xs text-neutral-500 mt-1">
              Akceptujemy formaty: 1234567890, 123-456-78-90, 123 456 78 90,
              PL1234567890
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Brand
            </label>
            <select
              value={formData.brandId || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  brandId: e.target.value || undefined,
                })
              }
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Wybierz brand (opcjonalnie)</option>
              {brands?.map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.displayName}
                </option>
              ))}
            </select>
          </div>

          {/* User Creation Section */}
          <div className="border-t border-neutral-200 pt-4">
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={createUser}
                onChange={(e) => handleUserToggle(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
              />
              <span className="text-sm font-medium text-neutral-700">
                Utwórz użytkownika dla tego tenanta
              </span>
            </label>
          </div>

          {createUser && (
            <div className="space-y-4 bg-neutral-50 p-4 rounded-md">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Imię *
                  </label>
                  <input
                    type="text"
                    required={createUser}
                    value={formData.userFirstName || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        userFirstName: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Imię"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Nazwisko *
                  </label>
                  <input
                    type="text"
                    required={createUser}
                    value={formData.userLastName || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, userLastName: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Nazwisko"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  required={createUser}
                  value={formData.userEmail || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, userEmail: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="email@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Hasło (opcjonalne)
                </label>
                <input
                  type="password"
                  value={formData.userPassword || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, userPassword: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Minimum 8 znaków"
                  minLength={8}
                />
                <p className="text-xs text-neutral-500 mt-1">
                  Jeśli nie ustawisz hasła, użytkownik otrzyma link do logowania
                  na email
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-neutral-600 hover:text-neutral-800 font-medium transition-colors"
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md font-medium transition-colors"
            >
              {isLoading ? "Dodawanie..." : "Dodaj"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


export default TenantManagement;
